import axios from "axios";
import { jwtDecode } from "jwt-decode";

// 環境變數中加載 API 基本 URL
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// 創建 axios 實例
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  }
});

// 請求攔截器：加入用戶 email
apiClient.interceptors.request.use(
  (config) => {
    const user = getCurrentUser();
    if (user?.email) {
      config.headers["X-User-Email"] = user.email;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 響應攔截器：統一處理錯誤
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error("API Response Error:", error.response.data);
    } else if (error.request) {
      console.error("API Request Error:", error.request);
    } else {
      console.error("API Unexpected Error:", error.message);
    }
    return Promise.reject(error);
  }
);

// 通用 API 函數
export const fetchData = async (endpoint, config = {}) => {
  try {
    const response = await apiClient.get(endpoint, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const postData = async (endpoint, data, config = {}) => {
  try {
    const response = await apiClient.post(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const putData = async (endpoint, data, config = {}) => {
  try {
    const response = await apiClient.put(endpoint, data, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteData = async (endpoint, config = {}) => {
  try {
    const response = await apiClient.delete(endpoint, config);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// 分店相關 API
export const getBranches = async () => {
  try {
    const response = await apiClient.get("/api/branches");
    return response.data;
  } catch (error) {
    console.error("Error fetching branches:", error);
    throw error;
  }
};

export const getBranchByName = async (name) => {
  try {
    const response = await apiClient.get("/api/branches/name", {
      params: { name },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching branch by name:", error);
    throw error;
  }
};

// 用戶相關 API
export const logUserLogin = async ({ name, email, ip_address, browser_info, login_time }) => {
  try {
    const response = await apiClient.post('/api/log-login', {
      name,
      email,
      ip_address,
      browser_info,
      login_time
    });
    return response.data;
  } catch (error) {
    console.error('Error logging user login:', error);
    return null;
  }
};

export const getLoginHistory = async (email) => {
  try {
    const response = await apiClient.get("/api/log-history", {
      params: { email },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching login history:", error);
    throw error;
  }
};

export const recordAiUsage = async () => {
  try {
    const user = getCurrentUser();
    if (!user) return null;

    const response = await apiClient.post('/api/record-ai-usage', {
      email: user.email
    });
    return response.data;
  } catch (error) {
    console.error('Error recording AI usage:', error);
    return null;
  }
};

// 日記相關 API
export const getDiaries = async () => {
  try {
    const user = getCurrentUser();
    const headers = user ? { "X-User-Email": user.email } : {};

    const response = await apiClient.get('/api/diaries', { headers });
    return response.data.diaries || [];
  } catch (error) {
    console.error('Error fetching diaries:', error);
    return [];
  }
};


export const getDiaryById = async (id) => {
  try {
    const user = getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    const response = await apiClient.get(`/api/diaries/${id}`, {
      headers: { "X-User-Email": user.email } // 用 Header 傳遞 email，避免 URL 曝露
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching diary:", error);
    throw error;
  }
};

export const createDiary = async (diaryData) => {
  try {
    const user = getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    const today = new Date();
    const localDate = today.toLocaleDateString('zh-TW', { 
      year: 'numeric', 
      month: '2-digit', 
      day: '2-digit' 
    }).replace(/\//g, '-');

    const response = await apiClient.post('/api/diaries', {
      ...diaryData,
      email: user.email,
      date: diaryData.date || localDate,
      is_public: diaryData.is_public || false  // 添加 is_public 欄位
    });
    return response.data;
  } catch (error) {
    console.error('Error creating diary:', error);
    throw error;
  }
};

export const updateDiary = async (id, diaryData) => {
  try {
    const user = getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    const response = await apiClient.put(`/api/diaries/${id}`, {
      ...diaryData,
      email: user.email,
      is_public: diaryData.is_public  // 添加 is_public 欄位
    });
    return response.data;
  } catch (error) {
    console.error('Error updating diary:', error);
    throw error;
  }
};

export const deleteDiary = async (id) => {
  try {
    const user = getCurrentUser();
    if (!user) throw new Error('User not authenticated');

    const response = await apiClient.delete(`/api/diaries/${id}`, {
      data: { email: user.email }  // 透過 body 傳遞 email
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting diary:', error);
    throw error;
  }
};

// Google 登入相關
export const handleGoogleLogin = async (response) => {
  try {
    // 檢查是否已經登入
    const currentUser = getCurrentUser();
    if (currentUser?.email) {
      return currentUser;
    }

    const decoded = jwtDecode(response.credential);
    const user = {
      email: decoded.email,
      name: decoded.name,
      picture: decoded.picture
    };
    sessionStorage.setItem('user', JSON.stringify(user));
    
    // 記錄登入，添加所需的欄位
    await logUserLogin({
      name: user.name,
      email: user.email,
      ip_address: await getIpAddress(),
      browser_info: navigator.userAgent,
      login_time: new Date().toISOString()
    });

    return user;
  } catch (error) {
    console.error('Error in Google login:', error);
    throw error;
  }
};

// 獲取用戶 IP 地址
const getIpAddress = async () => {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Error getting IP address:', error);
    return 'unknown';
  }
};

export const logout = () => {
  sessionStorage.removeItem('user');
};

export const getCurrentUser = () => {
  const userStr = sessionStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

// 圖片上傳
export const uploadImage = async (file) => {
  try {
    const formData = new FormData();
    formData.append('image', file);

    const response = await apiClient.post('/api/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data.imageUrl; // 確保後端回傳圖片 URL
  } catch (error) {
    if (error.response?.status === 413) {
      console.error('Image is too large!');
      throw new Error('圖片過大，請選擇較小的圖片。');
    }
    console.error('Error uploading image:', error);
    throw error;
  }
};


